import { HttpApiEndpoint, HttpApiGroup, HttpApiSchema } from "@effect/platform";
import * as S from "effect/Schema";
import { NotFound } from "./folders.js";
import { PagingParams, pagedResponse } from "./paging.js";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
import { PlainDateFromString } from "./temporal.js";
import { LanguageSchema } from "./column-schemas.js";
/**
 * Wraps `apps/api`'s `/dearest/*` controller (a proxy in front of the
 * dearest-api service, which itself is Timekit's only caller) 1:1 with typed
 * DTOs for the dashboard/employee booking screens. Every Timekit-backed
 * operation stays behind this endpoint → dearest-api → Timekit chain — no
 * Timekit URL or key is ever named on this contract or reaches a browser
 * bundle.
 */
export const DearestLanguage = LanguageSchema;
export const DearestGender = S.Literal("female", "male");
export const DearestCategoryKey = S.Literal("health", "career", "finance", "family", "addiction", "mental", "care", "law");
export const DearestConsultationType = S.Literal("phone", "video", "onsite");
export const DearestBookingStatus = S.Literal("pending", "confirmed", "completed", "cancelled");
export const DearestRating = S.Literal("great", "okay", "switchCoach");
export const DearestFeedback = S.Struct({
    rating: DearestRating,
    comment: S.optional(S.String),
    submitted_at: S.String,
});
export const DearestCoachLocationSummary = S.Struct({
    name: S.optional(S.String),
    street: S.optional(S.String),
    house_number: S.optional(S.String),
    postal_code: S.optional(S.String),
    city: S.optional(S.String),
});
/**
 * `dearest-api`'s `bookings` row, remapped onto the web's naming (strips
 * `timekit_booking_id` and other internal fields — the mapping the legacy
 * controller already performs, reused verbatim by the live layer).
 */
export const DearestBooking = S.Struct({
    id: S.UUID,
    user_id: S.UUID,
    category_key: S.String,
    is_first_session: S.Boolean,
    language: DearestLanguage,
    consultation_type: DearestConsultationType,
    phone_number: S.optional(S.String),
    coach_id: S.UUID,
    coach_location_id: S.optional(S.UUID),
    coach_location: S.optional(DearestCoachLocationSummary),
    slot_iso: S.String,
    status: DearestBookingStatus,
    created_at: S.String,
    cancelled_at: S.optional(S.String),
    completed_at: S.optional(S.String),
    video_call_url: S.optional(S.String),
    feedback: S.optional(DearestFeedback),
});
export const DearestCoach = S.Struct({
    id: S.UUID,
    name: S.String,
    gender: DearestGender,
    languages: S.Array(DearestLanguage),
    category_keys: S.Array(S.String),
    bio: S.optional(S.String),
    education: S.optional(S.String),
    // A same-origin proxy path (`getAsset`'s own path), never an absolute
    // dearest-api/Timekit URL — mapDearestCoach already enforces this today.
    profile_picture: S.optional(S.String),
    previously_spoken: S.Boolean,
});
export const DearestCoachLocation = S.Struct({
    id: S.UUID,
    coach_id: S.UUID,
    name: S.String,
    street: S.optional(S.String),
    house_number: S.optional(S.String),
    postal_code: S.optional(S.String),
    city: S.optional(S.String),
    country: S.optional(S.String),
});
export const DearestTimeSlot = S.Struct({
    iso: S.String,
    label: S.String,
    // Set only by the aggregated "any coach" availability — the coach that
    // owns the slot, carried so booking can target a concrete coach without
    // revealing them up front.
    coach_id: S.optional(S.UUID),
});
export const DearestAvailability = S.Struct({
    available_dates: S.Array(S.String),
    slots_by_date: S.Record({ key: S.String, value: S.Array(DearestTimeSlot) }),
});
export const DearestClientSettings = S.Struct({
    dearest_booking_virtual_enabled: S.Boolean,
    dearest_booking_onsite_enabled: S.Boolean,
});
/**
 * dearest-api did not answer, timed out, or failed (5xx, 429), so a Dearest
 * read or write could not be served right now. The web renders it as the
 * Dearest area's "temporarily unavailable" panel; the rest of the portal
 * does not depend on dearest-api. A malformed request or response is a
 * defect, never this.
 */
export const DearestUnavailable = S.Struct({
    _tag: S.Literal("DearestUnavailable"),
});
export const DearestMeResponse = S.Struct({
    has_account: S.Boolean,
    has_dearest_share: S.Boolean,
    user_email: S.NullOr(S.String),
    /**
     * The caller's own Dearest claim code, the proof the Dearest app's
     * access-code registration accepts (PMO-647). Read from Dearest for the
     * signed-in principal only, and only while they have a share but no
     * Dearest account yet; `null` otherwise. Never the portal registration
     * code (`user_metadata.unique_key`), which officers can see.
     */
    dearest_code: S.NullOr(S.String),
    /**
     * The claim code was needed but Dearest could not be reached, so
     * `dearest_code` is `null` for that reason alone. Everything else in this
     * response is read from PimaOne's own database and stays correct during
     * a Dearest outage.
     */
    dearest_unavailable: S.Boolean,
    client_settings: DearestClientSettings,
});
// ---------------------------------------------------------------------------
// dearest.getMe
// ---------------------------------------------------------------------------
export const GetMe = HttpApiEndpoint.get("getMe", "/dearest/me").addSuccess(S.Struct({ data: DearestMeResponse }));
// ---------------------------------------------------------------------------
// dearest.createAccount
// ---------------------------------------------------------------------------
/**
 * The signed-in employee creates their Dearest account from the web
 * (PMO-371). It signs in to the app with the portal address and the portal
 * password. An employee without a portal password (single sign-on only)
 * chooses one for the app here.
 */
export const CreateDearestAccountPayload = S.Struct({
    password: S.optional(S.String.pipe(S.minLength(8), S.maxLength(256))),
});
/** Nothing was shared with this employee, so there is no access to claim. */
export const NoDearestShare = S.Struct({ _tag: S.Literal("NoDearestShare") });
/** The employee, or the portal address, already has a Dearest account. */
export const DearestAccountExists = S.Struct({
    _tag: S.Literal("DearestAccountExists"),
});
/** No portal password to share, and none chosen for the app. */
export const DearestPasswordRequired = S.Struct({
    _tag: S.Literal("DearestPasswordRequired"),
});
export const CreateAccount = HttpApiEndpoint.post("createAccount", "/dearest/account")
    .setPayload(CreateDearestAccountPayload)
    .addSuccess(S.Struct({ data: S.Struct({ created: S.Literal(true) }) }))
    .addError(NoDearestShare, { status: 404 })
    .addError(DearestAccountExists, { status: 409 })
    .addError(DearestPasswordRequired, { status: 400 })
    .addError(DearestUnavailable, { status: 503 });
// ---------------------------------------------------------------------------
// dearest.listBookings / getBooking / createBooking / updateBooking /
// cancelBooking
// ---------------------------------------------------------------------------
export const DearestBookingStatusGroup = S.Literal("current", "archive");
export const ListBookingsParams = S.extend(PagingParams, S.Struct({
    status_group: S.optionalWith(DearestBookingStatusGroup, {
        default: () => "current",
    }),
}));
export const ListBookings = HttpApiEndpoint.get("listBookings", "/dearest/bookings")
    .setUrlParams(ListBookingsParams)
    .addSuccess(pagedResponse(DearestBooking))
    .addError(Forbidden, { status: 403 })
    .addError(DearestUnavailable, { status: 503 });
export const GetBookingParams = S.Struct({ id: S.UUID });
/** The booking id doesn't exist. Ownership mismatch is `Forbidden`, not this. */
export const BookingNotFound = S.Struct({ _tag: S.Literal("BookingNotFound") });
export const GetBooking = HttpApiEndpoint.get("getBooking", "/dearest/bookings/get")
    .setUrlParams(GetBookingParams)
    .addSuccess(S.Struct({ data: DearestBooking }))
    .addError(BookingNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 })
    .addError(DearestUnavailable, { status: 503 });
export const CreateBookingPayload = S.Struct({
    category_key: DearestCategoryKey,
    is_first_session: S.Boolean,
    language: DearestLanguage,
    consultation_type: DearestConsultationType,
    phone_number: S.optional(S.String),
    coach_location_id: S.optional(S.UUID),
    coach_id: S.UUID,
    slot_iso: S.String,
});
/** The caller's client has this consultation channel disabled (module gate). */
export const ConsultationTypeDisabled = S.Struct({
    _tag: S.Literal("ConsultationTypeDisabled"),
});
export const CreateBooking = HttpApiEndpoint.post("createBooking", "/dearest/bookings/create")
    .setPayload(CreateBookingPayload)
    .addSuccess(S.Struct({ data: DearestBooking }))
    .addError(ConsultationTypeDisabled, { status: 403 })
    .addError(Forbidden, { status: 403 })
    .addError(DearestUnavailable, { status: 503 });
/**
 * Only `metadata` (feedback) is writable — status changes go through
 * `cancelBooking`, matching the legacy controller's own restriction.
 */
export const UpdateBookingPayload = S.Struct({
    id: S.UUID,
    metadata: S.Record({ key: S.String, value: S.Unknown }),
});
export const UpdateBooking = HttpApiEndpoint.post("updateBooking", "/dearest/bookings/update")
    .setPayload(UpdateBookingPayload)
    .addSuccess(S.Struct({ data: DearestBooking }))
    .addError(BookingNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 })
    .addError(DearestUnavailable, { status: 503 });
export const CancelBookingPayload = S.Struct({ id: S.UUID });
export const CancelBooking = HttpApiEndpoint.post("cancelBooking", "/dearest/bookings/cancel")
    .setPayload(CancelBookingPayload)
    .addSuccess(S.Struct({ data: DearestBooking }))
    .addError(BookingNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 })
    .addError(DearestUnavailable, { status: 503 });
// ---------------------------------------------------------------------------
// dearest.listCoaches / getCoach / listCoachLocations
// ---------------------------------------------------------------------------
export const ListCoachesParams = S.Struct({
    category: S.optional(S.String),
    language: S.optional(S.String),
});
export const ListCoaches = HttpApiEndpoint.get("listCoaches", "/dearest/coaches")
    .setUrlParams(ListCoachesParams)
    .addSuccess(S.Struct({ items: S.Array(DearestCoach), total: S.Int }))
    .addError(DearestUnavailable, { status: 503 });
export const GetCoachParams = S.Struct({ id: S.UUID });
export const CoachNotFound = S.Struct({ _tag: S.Literal("CoachNotFound") });
export const GetCoach = HttpApiEndpoint.get("getCoach", "/dearest/coaches/get")
    .setUrlParams(GetCoachParams)
    .addSuccess(S.Struct({ data: DearestCoach }))
    .addError(CoachNotFound, { status: 404 })
    .addError(DearestUnavailable, { status: 503 });
export const ListCoachLocationsParams = S.Struct({
    category: S.optional(S.String),
});
export const ListCoachLocations = HttpApiEndpoint.get("listCoachLocations", "/dearest/coaches/locations")
    .setUrlParams(ListCoachLocationsParams)
    .addSuccess(S.Struct({ items: S.Array(DearestCoachLocation), total: S.Int }))
    .addError(DearestUnavailable, { status: 503 });
// ---------------------------------------------------------------------------
// dearest.getCoachAvailability / getAnyCoachAvailability
// ---------------------------------------------------------------------------
export const GetCoachAvailabilityParams = S.Struct({
    coach_id: S.UUID,
    from: S.optional(PlainDateFromString),
    to: S.optional(PlainDateFromString),
    date: S.optional(PlainDateFromString),
});
export const GetCoachAvailability = HttpApiEndpoint.get("getCoachAvailability", "/dearest/coaches/availability/for-coach")
    .setUrlParams(GetCoachAvailabilityParams)
    .addSuccess(S.Struct({ data: DearestAvailability }))
    .addError(DearestUnavailable, { status: 503 });
/**
 * Aggregated "any coach / next available session" availability, fanned out
 * across every coach eligible for `category`/`language` — the candidate
 * pool is the same filter `listCoaches` applies.
 */
export const GetAnyCoachAvailabilityParams = S.Struct({
    category: S.optional(S.String),
    language: S.optional(S.String),
    from: S.optional(PlainDateFromString),
    to: S.optional(PlainDateFromString),
    date: S.optional(PlainDateFromString),
});
export const GetAnyCoachAvailability = HttpApiEndpoint.get("getAnyCoachAvailability", "/dearest/coaches/availability/any")
    .setUrlParams(GetAnyCoachAvailabilityParams)
    .addSuccess(S.Struct({ data: DearestAvailability }))
    .addError(DearestUnavailable, { status: 503 });
// ---------------------------------------------------------------------------
// dearest.getAsset — authenticated blob (coach profile pictures)
// ---------------------------------------------------------------------------
export const GetAssetParams = S.Struct({ id: S.UUID });
/**
 * Raw bytes for one dearest-api asset — same-origin only, never a direct
 * dearest-api/Timekit host in a browser bundle. Fixed `application/
 * octet-stream`, matching `files.downloadFile`'s own tradeoff on this
 * contract: the caller already knows the real mimetype from wherever it
 * found this id (a coach profile) and sets its own blob type from that.
 */
export const GetAsset = HttpApiEndpoint.get("getAsset", "/dearest/assets/get")
    .setUrlParams(GetAssetParams)
    .addSuccess(HttpApiSchema.Uint8Array({ contentType: "application/octet-stream" }))
    .addError(NotFound, { status: 404 })
    .addError(DearestUnavailable, { status: 503 });
// ---------------------------------------------------------------------------
// dearest.sendAdminNotification
// ---------------------------------------------------------------------------
export const SendAdminNotificationPayload = S.Struct({
    subject: S.NonEmptyString,
    message: S.NonEmptyString,
});
export const SendAdminNotification = HttpApiEndpoint.post("sendAdminNotification", "/dearest/notifications/admin")
    .setPayload(SendAdminNotificationPayload)
    .addSuccess(S.Struct({ data: S.Struct({ ok: S.Literal(true) }) }))
    .addError(DearestUnavailable, { status: 503 });
// ---------------------------------------------------------------------------
// What dearest-api reads from PIMA One (served by the dearestInternal group)
// ---------------------------------------------------------------------------
export const DearestSettings = S.Struct({
    client_id: S.UUID,
    dearest_enabled: S.Boolean,
    dearest_external_categories_enabled: S.Boolean,
    dearest_booking_virtual_enabled: S.Boolean,
    dearest_booking_onsite_enabled: S.Boolean,
});
export const ExternalSignupEligibilityPayload = S.Struct({
    email: S.String,
});
export const DearestEligibilityReason = S.Literal("ALLOWED", "INVALID_EMAIL", "NO_MATCH", "DOMAIN_INACTIVE", "DEAREST_DISABLED", "EXTERNAL_DISABLED", "AMBIGUOUS_MATCH");
export const DearestEligibilityResponse = S.Struct({
    allowed: S.Boolean,
    client_id: S.NullOr(S.UUID),
    dearest_enabled: S.NullOr(S.Boolean),
    /** The matched customer's domain sign-up switch, under the name dearest-api reads. */
    dearest_external_signup_enabled: S.NullOr(S.Boolean),
    reason: DearestEligibilityReason,
});
export const DeleteSharesResult = S.Struct({
    deleted_user_ids: S.Array(S.UUID),
});
export const dearestGroup = HttpApiGroup.make("dearest")
    .add(GetMe)
    .add(CreateAccount)
    .add(ListBookings)
    .add(GetBooking)
    .add(CreateBooking)
    .add(UpdateBooking)
    .add(CancelBooking)
    .add(ListCoaches)
    .add(GetCoach)
    .add(ListCoachLocations)
    .add(GetCoachAvailability)
    .add(GetAnyCoachAvailability)
    .add(GetAsset)
    .add(SendAdminNotification)
    .middleware(PrincipalMiddleware);
const routesOutsideRequestTransaction = new Set(Object.values(dearestGroup.endpoints).map((endpoint) => `${endpoint.method} /v1${endpoint.path}`));
/**
 * Whether the bridge runs this route's handler outside the request
 * transaction. The Dearest group's handlers wait on dearest-api, and a request
 * transaction holds a pooled database connection while they wait, so a slow
 * or hanging Dearest would starve the rest of the portal of connections
 * (PMO-377). Their handlers read the database in short transactions of their
 * own (dearest.router.ts). `path` is the bridged path without its query.
 *
 * The router answers a HEAD with the GET handler of the same path when no
 * HEAD route is declared, so a HEAD runs wherever that GET runs.
 */
export const runsOutsideRequestTransaction = (method, path) => routesOutsideRequestTransaction.has(`${method} ${path}`) ||
    (method === "HEAD" && routesOutsideRequestTransaction.has(`GET ${path}`));
//# sourceMappingURL=dearest.js.map